import { Button, styled, Typography } from '@mui/material'
import { useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import { Box, Modal, ProvisioningStatus } from '@/components/ui'
import type { LocationDetail } from '@/definitions/location'
import type { OrganizationDetail } from '@/definitions/organization'
import { STAFF_ERROR_CODES } from '@/definitions/staff'
import { useCreateLocation } from '@/hooks/useCreateLocation'
import { useCreateOrganization } from '@/hooks/useCreateOrganization'
import { useCreateStaff } from '@/hooks/useCreateStaff'
import { useSnackbar } from '@/hooks/useSnackbar'
import { spacing } from '@/themes/tokens'
import { getApiErrorCode, getApiErrorMessage, isConflictError } from '@/utils/apiErrors'
import { buildLocationRequest } from '@/utils/buildLocationRequest'

import {
  type CreateOrgFormValues,
  defaultFormValues,
} from './definitions/createOrganizationForm'
import { AdminUserStep } from './steps/AdminUserStep'
import { BasicInfoStep } from './steps/BasicInfoStep'
import { BillingTaxStep } from './steps/BillingTaxStep'
import { LocationStep } from './steps/LocationStep'
import { ReviewStep } from './steps/ReviewStep'
import { buildOrganizationRequest } from './utils/buildOrganizationRequest'
import { buildStaffRequest } from './utils/buildStaffRequest'
import { getStepValidity } from './utils/getStepValidity'
import {
  ADMIN_STEP,
  getFirstReachableStep,
  isStepClosed,
  LOCATION_STEP,
  REVIEW_STEP,
  SETUP_STEPS,
  TAX_STEP,
} from './utils/setupSteps'

/**
 * The organization whose setup is being finished, read by the caller beforehand so
 * the flow opens at the right step on its first render.
 */
export interface ResumeSetupTarget {
  organization: OrganizationDetail
  /** The primary location, or null when the organization has none yet. */
  existingLocation: LocationDetail | null
  hasStaff: boolean
}

interface OrganizationSetupModalProps {
  onClose: () => void
  /** Given to finish an existing organization's setup; omitted to start a new one. */
  resume?: ResumeSetupTarget
}

const ORG_PROVISIONING_MESSAGE =
  'Creating the organization — provisioning its storage and access. This takes a moment.'
const LOCATION_PROVISIONING_MESSAGE = 'Adding the primary location…'
const LOCATION_FAILED_MESSAGE = 'The location could not be created.'
const ADMIN_PROVISIONING_MESSAGE = 'Creating the admin user…'
const ADMIN_FAILED_MESSAGE = 'The admin user could not be created.'

const ProgressSegment = styled('div', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  flex: 1,
  height: 6,
  borderRadius: 3,
  backgroundColor: active ? theme.palette.primary.main : theme.palette.divider,
  transition: 'background-color 0.2s ease',
}))

const ProgressContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(spacing.xs),
  marginTop: theme.spacing(spacing.md),
  marginBottom: theme.spacing(spacing.xs),
}))

/**
 * The organization setup flow, whether it is starting or being picked back up.
 *
 * Each step commits its own record as the user leaves it rather than everything
 * landing at the end: the organization on leaving Tax, the location on leaving
 * Location, the admin user on leaving Admin User. None of the three can be
 * skipped — an organization with no location has no address, phone, or time zone,
 * and one nobody can sign in to is not a finished setup.
 *
 * Because the records land one at a time, a run that stops early leaves a real but
 * incomplete organization, which `resume` picks back up at the first record missing.
 * The ids held here mean a retry after a later failure cannot produce a second
 * organization or a second location.
 */
export const OrganizationSetupModal = ({
  onClose,
  resume,
}: OrganizationSetupModalProps) => {
  const { showSuccess } = useSnackbar()
  const { mutateAsync: createOrganization, isPending: isCreatingOrg } =
    useCreateOrganization()
  const { mutateAsync: createLocation, isPending: isCreatingLocation } = useCreateLocation()
  const { mutateAsync: createStaff, isPending: isCreatingStaff } = useCreateStaff()

  // Settled at mount from what the caller handed over, so the flow cannot land on a
  // step and then move off it.
  const [currentStep, setCurrentStep] = useState(() =>
    getFirstReachableStep({
      hasOrganization: resume !== undefined,
      hasLocation: resume?.existingLocation != null,
      hasAdmin: resume?.hasStaff ?? false,
    }))
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(
    resume?.organization.id ?? null
  )
  const [createdLocationId, setCreatedLocationId] = useState<string | null>(null)
  const [createdStaff, setCreatedStaff] = useState(false)
  const [locationFailureMessage, setLocationFailureMessage] = useState<string>()
  const [locationAlreadyExists, setLocationAlreadyExists] = useState(false)
  const [adminFailureMessage, setAdminFailureMessage] = useState<string>()
  // A resumed organization with staff cannot be given a first admin, so that step
  // opens on saying so rather than collecting details the endpoint would refuse.
  const [adminAlreadyExists, setAdminAlreadyExists] = useState(resume?.hasStaff ?? false)

  // The one this run created, or the one the organization already had.
  const primaryLocationId = createdLocationId ?? resume?.existingLocation?.id ?? null

  const firstReachableStep = getFirstReachableStep({
    hasOrganization: createdOrgId !== null,
    hasLocation: primaryLocationId !== null || locationAlreadyExists,
    hasAdmin: createdStaff || adminAlreadyExists,
  })

  // Held whole so it can be handed to FormProvider: the Location step's fields are
  // the shared `LocationFields`, which reads its form off context rather than
  // taking a control prop.
  const form = useForm<CreateOrgFormValues>({
    defaultValues: defaultFormValues,
    mode: 'onTouched',
  })
  const { control, getValues } = form

  // Subscribing to the whole form keeps step gating in sync as the user types,
  // without each step having to report its own validity upward.
  const values = useWatch({ control, defaultValue: defaultFormValues }) as CreateOrgFormValues
  const stepValidity = getStepValidity(values)

  // Read off the mutations rather than tracked alongside them. Each holds its own
  // minimum duration, so its pending state spans the whole wait the status is shown
  // for; the creates are sequential, so at most one is ever pending.
  const provisioningMessage = isCreatingOrg
    ? ORG_PROVISIONING_MESSAGE
    : isCreatingLocation
      ? LOCATION_PROVISIONING_MESSAGE
      : isCreatingStaff
        ? ADMIN_PROVISIONING_MESSAGE
        : undefined
  const isPending = provisioningMessage !== undefined
  const isReviewStep = currentStep === REVIEW_STEP
  const canLeaveCurrentStep =
    isStepClosed({ step: currentStep, locationAlreadyExists, adminAlreadyExists }) ||
    (stepValidity[currentStep] ?? true)
  const organizationName =
    resume?.organization.display_name ?? (values.display_name.trim() || 'The organization')
  // Derived once so the step that states the assignment and the one that reports it
  // cannot disagree with the request.
  const primaryLocationName = createdLocationId
    ? values.location_name.trim()
    : (resume?.existingLocation?.display_name ?? null)
  // A resumed run wrote neither the organization nor its tax details, so the review
  // says so rather than echoing them.
  const organizationOnRecord = resume !== undefined
  const locationOnRecord = resume?.existingLocation != null || locationAlreadyExists

  const advance = () => setCurrentStep((step) => step + 1)
  const handleBack = () => setCurrentStep((step) => Math.max(step - 1, firstReachableStep))

  const handleCreateOrg = async () => {
    const request = buildOrganizationRequest(getValues())
    try {
      const organization = await createOrganization(request)
      setCreatedOrgId(organization.id)
      showSuccess(`${request.display_name} was created successfully.`)
      advance()
    } catch {
      // Nothing has been created, so the form stays exactly as it is and the
      // global error toast is the whole of the report.
    }
  }

  const handleCreateLocation = async () => {
    if (!createdOrgId) {
      return
    }
    const request = buildLocationRequest(getValues())
    try {
      const location = await createLocation({ orgId: createdOrgId, data: request })
      setLocationFailureMessage(undefined)
      setCreatedLocationId(location.id)
      showSuccess(`${request.display_name} was added as the primary location.`)
      advance()
    } catch (error) {
      if (isConflictError(error)) {
        // The organization already has a location, so this write landed even
        // though its response did not. Retrying can only conflict again, so the
        // step reports what happened and offers the way forward instead.
        setLocationFailureMessage(undefined)
        setLocationAlreadyExists(true)
        return
      }
      // Reported on the step itself rather than as a toast: the organization
      // survived, and only this step can say so while offering the retry.
      setLocationFailureMessage(getApiErrorMessage(error, LOCATION_FAILED_MESSAGE))
    }
  }

  const handleCreateAdmin = async () => {
    if (!createdOrgId) {
      return
    }
    const request = buildStaffRequest(getValues(), primaryLocationId)
    try {
      await createStaff({ orgId: createdOrgId, data: request })
      setAdminFailureMessage(undefined)
      setCreatedStaff(true)
      showSuccess(`${request.name_given} ${request.name_family} was added as the admin.`)
      advance()
    } catch (error) {
      // A conflict means one of two things, and they want opposite handling: the
      // organization already has staff, which no retry can get past, or the
      // username is taken, which correcting it will. The endpoint names which one
      // it refused on, so neither the message nor a second call has to be read for
      // it. Anything else — including a conflict that names nothing — falls to the
      // correctable branch, where a retry is merely useless rather than misleading.
      if (getApiErrorCode(error) === STAFF_ERROR_CODES.ORG_ALREADY_HAS_STAFF) {
        setAdminFailureMessage(undefined)
        setAdminAlreadyExists(true)
        return
      }
      // Reported on the step itself rather than as a toast: the organization and
      // any location survived, and the server's own message — a taken username —
      // is what the user acts on.
      setAdminFailureMessage(getApiErrorMessage(error, ADMIN_FAILED_MESSAGE))
    }
  }

  const handleNext = () => {
    if (currentStep === TAX_STEP) {
      return handleCreateOrg()
    }
    if (currentStep === LOCATION_STEP) {
      // Nothing to commit when the location already exists — `firstReachableStep`
      // has already closed this step behind us.
      return locationAlreadyExists ? advance() : handleCreateLocation()
    }
    if (currentStep === ADMIN_STEP) {
      // Nothing to commit when the organization already has its admin —
      // `firstReachableStep` has already closed this step behind us.
      return adminAlreadyExists ? advance() : handleCreateAdmin()
    }
    advance()
  }

  const stepContent = [
    <BasicInfoStep key='basic' control={control} />,
    <BillingTaxStep key='tax' control={control} />,
    <LocationStep
      key='location'
      alreadyExists={locationAlreadyExists}
      failureMessage={locationFailureMessage}
      organizationName={organizationName}
      organizationOnRecord={organizationOnRecord}
    />,
    <AdminUserStep
      key='admin'
      alreadyExists={adminAlreadyExists}
      control={control}
      failureMessage={adminFailureMessage}
      organizationName={organizationName}
      organizationOnRecord={organizationOnRecord}
      primaryLocationName={primaryLocationName}
    />,
    <ReviewStep
      key='review'
      adminAlreadyExists={adminAlreadyExists}
      control={control}
      locationOnRecord={locationOnRecord}
      organizationOnRecord={organizationOnRecord}
      primaryLocationName={primaryLocationName}
    />,
  ]

  const locationActionLabel = locationAlreadyExists
    ? 'Continue'
    : locationFailureMessage
      ? 'Try Again'
      : 'Add Location'

  const adminActionLabel = adminAlreadyExists
    ? 'Continue'
    : adminFailureMessage
      ? 'Try Again'
      : 'Create Admin User'

  const primaryActionLabel = [
    'Next',
    'Create Organization',
    locationActionLabel,
    adminActionLabel,
    'Done',
  ][currentStep]

  const progressBar = (
    <>
      <ProgressContainer>
        {SETUP_STEPS.map((step, index) => (
          <ProgressSegment key={step} active={index <= currentStep} />
        ))}
      </ProgressContainer>
      <Typography variant='caption' color='text.secondary' display='block'>
        Step {currentStep + 1} of {SETUP_STEPS.length} — {SETUP_STEPS[currentStep]}
      </Typography>
    </>
  )

  const actions = (
    <Box variant='flexBetween' fullWidth>
      <Button variant='text' onClick={onClose} disabled={isPending}>
        {createdOrgId ? 'Close' : 'Cancel'}
      </Button>
      <Box variant='flex' gap='sm' fullWidth={false}>
        <Button
          variant='outlined'
          onClick={handleBack}
          disabled={currentStep <= firstReachableStep || isPending}
        >
          Back
        </Button>
        <Button
          variant='contained'
          onClick={isReviewStep ? onClose : handleNext}
          disabled={!isReviewStep && (!canLeaveCurrentStep || isPending)}
        >
          {primaryActionLabel}
        </Button>
      </Box>
    </Box>
  )

  return (
    <FormProvider {...form}>
      <Modal
        onClose={onClose}
        title={resume ? 'Resume Setup' : 'New Organization'}
        headerContent={progressBar}
        actions={actions}
        disableClose={isPending}
      >
        {provisioningMessage ? (
          <ProvisioningStatus message={provisioningMessage} />
        ) : (
          stepContent[currentStep]
        )}
      </Modal>
    </FormProvider>
  )
}
